<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SquaremeAPI from 'dashboard/api/squareme';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  formatMoney,
  isZeroMoney,
  matchState,
} from 'dashboard/helper/squareme';
import SquaremeActivity from './SquaremeActivity.vue';
import SquaremeEscalationList from './SquaremeEscalationList.vue';
import SquaremeEscalateDialog from './SquaremeEscalateDialog.vue';
import SquaremeTour from './SquaremeTour.vue';

// Read-only Squareme customer context for this conversation's contact, and
// escalations to Watchtower. Shown only while the squareme-next integration
// is switched on (ContactPanel checks the switch).
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const currentChat = useMapGetter('getSelectedChat');

const TOUR_SETTING = 'squareme_panel_tour_seen';
const TOUR_STEPS = [
  {
    target: 'match',
    title: 'SQUAREME_PANEL.TOUR.STEPS.PANEL.TITLE',
    body: 'SQUAREME_PANEL.TOUR.STEPS.PANEL.BODY',
  },
  {
    target: 'badges',
    title: 'SQUAREME_PANEL.TOUR.STEPS.BADGES.TITLE',
    body: 'SQUAREME_PANEL.TOUR.STEPS.BADGES.BODY',
  },
  {
    target: 'activity',
    title: 'SQUAREME_PANEL.TOUR.STEPS.ACTIVITY.TITLE',
    body: 'SQUAREME_PANEL.TOUR.STEPS.ACTIVITY.BODY',
  },
  {
    target: 'escalate',
    title: 'SQUAREME_PANEL.TOUR.STEPS.ESCALATE.TITLE',
    body: 'SQUAREME_PANEL.TOUR.STEPS.ESCALATE.BODY',
  },
  {
    target: 'escalations',
    title: 'SQUAREME_PANEL.TOUR.STEPS.ESCALATIONS.TITLE',
    body: 'SQUAREME_PANEL.TOUR.STEPS.ESCALATIONS.BODY',
  },
];

const loading = ref(true);
const loadError = ref('');
const match = ref(null);
const customer = ref(null);
const escalations = ref([]);
const allowedTypes = ref([]);
const escalationsError = ref(false);
const retrying = ref({});
const dialogRef = ref(null);
const activityRef = ref(null);
const tourRef = ref(null);

const state = computed(() => matchState(match.value));
const profile = computed(() => customer.value?.profile || {});
const restrictions = computed(() => customer.value?.restrictions || {});
const balances = computed(() => customer.value?.balances || null);

const toDate = iso => (iso ? messageTimestamp(Date.parse(iso) / 1000) : '');

const bonusBalance = computed(
  () =>
    balances.value?.wallets?.find(wallet => wallet.type === 'bonus')?.balance
);

const moneyRows = computed(() => {
  if (!balances.value) return [];
  const rows = [
    { key: 'SPENDABLE', money: balances.value.spendable, always: true },
    { key: 'BONUS', money: bonusBalance.value },
    { key: 'CARD_HOLDS', money: balances.value.notSpendable?.cardHolds },
    { key: 'SAVINGS', money: balances.value.notSpendable?.savings?.total },
    { key: 'OWED', money: balances.value.owed?.debts },
  ];
  return rows.filter(
    row => row.money && (row.always || !isZeroMoney(row.money))
  );
});

const cards = computed(() =>
  [
    ...(customer.value?.cards || []),
    ...(customer.value?.debitCards || []),
  ].filter(card => card.last4)
);

const badges = computed(() => {
  if (!customer.value) return [];
  const list = [];
  if (restrictions.value.restriction)
    list.push({ key: 'RESTRICTED', tone: 'ruby' });
  if (restrictions.value.hold) list.push({ key: 'ON_HOLD', tone: 'amber' });
  if (customer.value.employee?.linked)
    list.push({ key: 'EMPLOYEE', tone: 'blue' });
  if (customer.value.internal || profile.value.internal) {
    list.push({ key: 'INTERNAL', tone: 'slate' });
  }
  if (restrictions.value.riskFlags > 0) {
    list.push({
      key: 'RISK',
      tone: 'amber',
      params: { count: restrictions.value.riskFlags },
    });
  }
  return list;
});

const TONES = {
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  blue: 'bg-n-blue-3 text-n-blue-11',
  slate: 'bg-n-slate-3 text-n-slate-11',
};

// Private notes Relay adds for each escalation carry its reference.
const privateNotes = computed(() =>
  (currentChat.value?.messages || []).filter(
    message => message.private && typeof message.content === 'string'
  )
);

const notesByReference = computed(() =>
  Object.fromEntries(
    escalations.value.map(escalation => [
      escalation.reference,
      privateNotes.value.filter(note =>
        note.content.includes(escalation.reference)
      ),
    ])
  )
);

const loadEscalations = async () => {
  try {
    const { data } = await SquaremeAPI.escalations(props.conversationId);
    escalations.value = data.escalations || [];
    allowedTypes.value = data.allowed_types || [];
    escalationsError.value = false;
  } catch {
    escalationsError.value = true;
  }
};

const maybeStartTour = async () => {
  if (uiSettings.value?.[TOUR_SETTING]) return;
  await nextTick();
  tourRef.value?.start();
};

const load = async () => {
  loading.value = true;
  loadError.value = '';
  try {
    const [{ data }] = await Promise.all([
      SquaremeAPI.customer(props.conversationId),
      loadEscalations(),
    ]);
    match.value = data.match;
    customer.value = data.customer;
  } catch (e) {
    loadError.value = e?.response?.status === 404 ? 'NOT_FOUND' : 'ERROR';
  } finally {
    loading.value = false;
  }
  if (!loadError.value) maybeStartTour();
};

const refresh = () => {
  load();
  activityRef.value?.reload();
};

const openEscalation = preset => dialogRef.value?.open(preset);

const addEscalation = escalation => {
  escalations.value = [
    escalation,
    ...escalations.value.filter(existing => existing.id !== escalation.id),
  ];
};

const onCreated = escalation => {
  addEscalation(escalation);
  useAlert(t('SQUAREME_PANEL.DIALOG.SENT'));
};

const retry = async escalation => {
  retrying.value = { ...retrying.value, [escalation.id]: true };
  try {
    const { data } = await SquaremeAPI.escalate(props.conversationId, {
      actionType: escalation.action_type,
      payload: escalation.payload,
      reason: escalation.reason,
    });
    onCreated(data);
  } catch {
    useAlert(t('SQUAREME_PANEL.ESCALATIONS.RETRY_ERROR'));
  } finally {
    retrying.value = { ...retrying.value, [escalation.id]: false };
  }
};

// A refusal adds no private note, so check while anything is still being sent.
const PENDING_POLL_MS = 5000;
let pendingPoll = null;
watch(
  () => escalations.value.some(escalation => escalation.status === 'pending'),
  pending => {
    clearInterval(pendingPoll);
    pendingPoll = pending
      ? setInterval(loadEscalations, PENDING_POLL_MS)
      : null;
  }
);
onBeforeUnmount(() => clearInterval(pendingPoll));

const finishTour = () => updateUISettings({ [TOUR_SETTING]: true });

watch(() => props.conversationId, load, { immediate: true });

// The customer as squareme-next shows it now, without the loading state.
const reloadCustomer = async () => {
  try {
    const { data } = await SquaremeAPI.customer(props.conversationId);
    match.value = data.match;
    customer.value = data.customer;
  } catch {
    // Keep what is shown; Refresh reports the error.
  }
  activityRef.value?.reload();
};

// Each status change adds a private note: refresh when one arrives. An
// approval has changed the account (a freeze, a reversal), so the customer
// and their activity are read again too.
watch(
  () => privateNotes.value.length,
  async (count, previous) => {
    if (previous === undefined || count <= previous) return;
    const approvedBefore = new Set(
      escalations.value.filter(e => e.status === 'approved').map(e => e.id)
    );
    await loadEscalations();
    if (
      escalations.value.some(
        e => e.status === 'approved' && !approvedBefore.has(e.id)
      )
    ) {
      reloadCustomer();
    }
  }
);
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3 text-sm text-n-slate-12">
    <div class="flex items-center justify-between gap-2">
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('SQUAREME_PANEL.READ_ONLY') }}
      </p>
      <div class="flex shrink-0 gap-1">
        <Button
          size="xs"
          variant="ghost"
          color="slate"
          icon="i-lucide-compass"
          data-test-id="squareme-tour-start"
          :title="$t('SQUAREME_PANEL.TOUR.START')"
          :aria-label="$t('SQUAREME_PANEL.TOUR.START')"
          @click="tourRef?.start()"
        />
        <Button
          size="xs"
          variant="ghost"
          color="slate"
          icon="i-lucide-refresh-cw"
          :title="$t('SQUAREME_PANEL.REFRESH')"
          :aria-label="$t('SQUAREME_PANEL.REFRESH')"
          :disabled="loading"
          @click="refresh"
        />
      </div>
    </div>

    <div v-if="loading" class="flex items-center gap-2 text-n-slate-11">
      <Spinner :size="20" class="text-n-brand" />
      {{ $t('SQUAREME_PANEL.LOADING') }}
    </div>

    <div
      v-else-if="loadError"
      role="alert"
      data-test-id="squareme-error"
      class="flex flex-col items-start gap-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-n-ruby-11"
    >
      {{ $t(`SQUAREME_PANEL.${loadError}`) }}
      <Button
        size="xs"
        variant="faded"
        color="ruby"
        :label="$t('SQUAREME_PANEL.RETRY')"
        @click="load"
      />
    </div>

    <template v-else>
      <section
        data-squareme-tour="match"
        data-test-id="squareme-match"
        :data-match-state="state"
      >
        <div v-if="state === 'matched' && customer" class="flex flex-col gap-2">
          <div class="flex flex-col">
            <span class="text-base font-medium">
              {{ profile.name || $t('SQUAREME_PANEL.NO_NAME') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('SQUAREME_PANEL.MATCH.MATCHED_APP') }}
              ·
              {{
                customer.access?.pii
                  ? $t('SQUAREME_PANEL.ACCESS_FULL')
                  : $t('SQUAREME_PANEL.ACCESS_MASKED')
              }}
            </span>
          </div>
          <div
            data-squareme-tour="badges"
            data-test-id="squareme-badges"
            class="flex flex-wrap gap-1"
          >
            <span class="rounded bg-n-slate-3 px-1.5 text-xs text-n-slate-11">
              {{
                $t('SQUAREME_PANEL.BADGES.STATUS', { status: profile.status })
              }}
            </span>
            <span class="rounded bg-n-slate-3 px-1.5 text-xs text-n-slate-11">
              {{
                $t('SQUAREME_PANEL.BADGES.KYC_TIER', {
                  tier: customer.kyc?.tier ?? 0,
                })
              }}
            </span>
            <span
              v-for="badge in badges"
              :key="badge.key"
              :data-test-id="`squareme-badge-${badge.key}`"
              class="rounded px-1.5 text-xs font-medium"
              :class="TONES[badge.tone]"
            >
              {{ $t(`SQUAREME_PANEL.BADGES.${badge.key}`, badge.params || {}) }}
            </span>
          </div>
          <p
            v-if="restrictions.restriction"
            class="mb-0 text-xs text-n-ruby-11"
          >
            {{
              $t('SQUAREME_PANEL.RESTRICTION_REASON', {
                date: toDate(restrictions.restriction.since),
                reason: restrictions.restriction.reason,
              })
            }}
          </p>
          <p v-if="restrictions.hold" class="mb-0 text-xs text-n-amber-11">
            {{
              $t('SQUAREME_PANEL.HOLD_REASON', {
                date: toDate(restrictions.hold.since),
                reason: restrictions.hold.reason,
              })
            }}
          </p>
          <p
            v-if="customer.employee?.linked"
            class="mb-0 text-xs text-n-slate-11"
          >
            {{
              $t('SQUAREME_PANEL.EMPLOYEE_SINCE', {
                date: toDate(customer.employee.since),
              })
            }}
          </p>
          <dl
            data-test-id="squareme-profile"
            class="mb-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs"
          >
            <template v-if="profile.phone">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.PHONE') }}
              </dt>
              <dd class="mb-0 break-all">{{ profile.phone }}</dd>
            </template>
            <template v-if="profile.email">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.EMAIL') }}
              </dt>
              <dd class="mb-0 break-all">{{ profile.email }}</dd>
            </template>
            <template v-if="profile.tag">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.TAG') }}
              </dt>
              <dd class="mb-0">{{ profile.tag }}</dd>
            </template>
            <dt class="text-n-slate-11">
              {{ $t('SQUAREME_PANEL.FIELDS.JOINED') }}
            </dt>
            <dd class="mb-0">{{ toDate(profile.joinedAt) }}</dd>
            <template v-for="row in moneyRows" :key="row.key">
              <dt class="text-n-slate-11">
                {{ $t(`SQUAREME_PANEL.FIELDS.${row.key}`) }}
              </dt>
              <dd
                class="mb-0 font-medium"
                :data-test-id="`squareme-money-${row.key}`"
              >
                {{ formatMoney(row.money) }}
              </dd>
            </template>
            <template v-if="customer.kyc?.submissions?.waitingForReview">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.KYC_WAITING') }}
              </dt>
              <dd class="mb-0">
                {{ customer.kyc.submissions.waitingForReview }}
              </dd>
            </template>
            <template v-if="customer.openDisputes?.length">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.OPEN_DISPUTES') }}
              </dt>
              <dd class="mb-0">{{ customer.openDisputes.length }}</dd>
            </template>
            <template v-if="cards.length">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.FIELDS.CARDS') }}
              </dt>
              <dd class="mb-0 flex flex-col">
                <span v-for="card in cards" :key="card.id">
                  {{
                    $t('SQUAREME_PANEL.CARD', {
                      brand: card.brand || '',
                      last4: card.last4,
                      status: card.status,
                    })
                  }}
                </span>
              </dd>
            </template>
          </dl>
        </div>
        <p
          v-else
          class="mb-0 rounded-lg bg-n-slate-2 px-3 py-2 text-n-slate-11"
        >
          {{ $t(`SQUAREME_PANEL.MATCH.${state.toUpperCase()}`) }}
        </p>
      </section>

      <SquaremeActivity
        v-if="state === 'matched' && customer"
        ref="activityRef"
        :conversation-id="conversationId"
        :allowed-types="allowedTypes"
        @escalate="openEscalation"
      />

      <section
        data-squareme-tour="escalations"
        data-test-id="squareme-escalations"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between gap-2">
          <h4 class="mb-0 text-sm font-medium">
            {{ $t('SQUAREME_PANEL.ESCALATIONS.TITLE') }}
          </h4>
        </div>
        <Button
          v-if="state === 'matched' && customer && allowedTypes.length"
          size="sm"
          variant="faded"
          color="amber"
          icon="i-lucide-shield-alert"
          data-squareme-tour="escalate"
          data-test-id="squareme-escalate"
          :label="$t('SQUAREME_PANEL.ESCALATIONS.ESCALATE')"
          @click="openEscalation()"
        />
        <p v-else-if="state !== 'matched'" class="mb-0 text-xs text-n-slate-11">
          {{ $t('SQUAREME_PANEL.ESCALATIONS.NEEDS_MATCH') }}
        </p>
        <p v-if="escalationsError" class="mb-0 text-xs text-n-ruby-11">
          {{ $t('SQUAREME_PANEL.ESCALATIONS.LOAD_ERROR') }}
        </p>
        <p v-else-if="!escalations.length" class="mb-0 text-xs text-n-slate-11">
          {{ $t('SQUAREME_PANEL.ESCALATIONS.EMPTY') }}
        </p>
        <SquaremeEscalationList
          v-else
          :escalations="escalations"
          :notes-by-reference="notesByReference"
          :retrying="retrying"
          @retry="retry"
        />
      </section>
    </template>

    <SquaremeEscalateDialog
      v-if="customer"
      ref="dialogRef"
      :conversation-id="conversationId"
      :customer="customer"
      :allowed-types="allowedTypes"
      @created="onCreated"
      @refused="addEscalation"
    />
    <SquaremeTour ref="tourRef" :steps="TOUR_STEPS" @finish="finishTour" />
  </div>
</template>
